import ReactSelect, {
  ActionMeta,
  components,
  GroupBase,
  InputActionMeta,
  MultiValue,
  OptionProps,
} from "react-select";
import { Checkbox } from "./Checkbox";
import { useCallback, useMemo } from "react";
import { cn } from "@/utils";

type OptionType = { [key: string]: any };
type OptionsType = Array<OptionType>;

interface Props {
  label?: string;
  name?: string;
  value?: OptionsType;
  defaultValue?: OptionsType;
  isDisabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  isRtl?: boolean;
  isSearchable?: boolean;
  options: OptionsType;
  className?: string;
  placeholder?: string;
  width?: string;
  height?: string;
  required?: boolean;
  error?: string;
  onMenuScrollToBottom?: ((event: WheelEvent | TouchEvent) => void) | undefined
  onInputChange?: ((newValue: string, actionMeta: InputActionMeta) => void) | undefined;
  onChange?: (
    newValue: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => void;
}

const selectAllOption = {
  value: "__SELECT_ALL__",
  label: "すべて選択",
};

export const CheckboxMultiSelect = ({
  label,
  name,
  value,
  defaultValue,
  isDisabled = false,
  isLoading = false,
  isClearable = false,
  isRtl = false,
  isSearchable = false,
  options,
  className,
  placeholder = "選択してください",
  width = "100%",
  height = "2.25rem",
  onChange,
  required,
  error,
  onMenuScrollToBottom,
  onInputChange,
}: Props) => {
  const isAllSelected = useMemo(() => {
    return value?.length === options.length;
  }, [value, options]);

  // TODO: remove sellect all
  // const extendedOptions = useMemo(() => {
  //   return options.length ? [selectAllOption, ...options] : [];
  // }, [options]);


  const handleChange = useCallback(
    (selected: MultiValue<OptionType>, actionMeta: ActionMeta<OptionType>) => {
      const isChooseAllSelected = selected.some(
        (item) => item.value === selectAllOption.value
      );
      if (isChooseAllSelected) {
        if (isAllSelected) {
          onChange?.([], actionMeta);
        } else {
          onChange?.(options, actionMeta);
        }
        return;
      }
      onChange?.(selected, actionMeta);
    },
    [onChange, isAllSelected, options]
  );

  const Option = useCallback(
    (props: OptionProps<OptionType, true, GroupBase<OptionType>>) => {
      if (props.data.value === selectAllOption.value) {
        return (
          <components.Option {...props}>
            <div className="flex items-center justify-between gap-2">
              <label className="truncate flex-1">{props.label}</label>
              <Checkbox
                checked={isAllSelected}
                aria-label={props.label}
                onChange={() => null}
              />
            </div>
          </components.Option>
        );
      }
      return (
        <components.Option {...props}>
          <div className="flex items-center justify-between gap-2">
            <label className="truncate flex-1">{props.label}</label>
            <Checkbox
              checked={props.isSelected}
              aria-label={props.label}
              onChange={() => null}
            />
          </div>
        </components.Option>
      );
    },
    [isAllSelected]
  );

  return (
    <div className={cn(`relative ${className} ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`)}>
      {label && (
        <label
          className={`
                  absolute left-4 top-0 -translate-y-1/2 px-1 text-xs z-[2] leading-3 pointer-events-none
                  ${error ? "text-error_main" : "text-grey-600"}
                `}
          style={{
            background: isDisabled
              ? "linear-gradient(to bottom, transparent 50%, #F4F6F8 50%)"
              : "#ffffff",
          }}
        >
          {label}
          {required && <span className="text-error_main ml-1">*</span>}
        </label>
      )}

      <ReactSelect
        components={{ Option }}
        className={`basic-multi-select`}
        classNamePrefix="select"
        isMulti={true}
        defaultValue={defaultValue}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        isRtl={isRtl}
        isSearchable={isSearchable}
        name={name}
        value={value}
        options={options?.length ? options : []}
        placeholder={placeholder}
        onChange={handleChange}
        onInputChange={onInputChange}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        onMenuScrollToBottom={onMenuScrollToBottom}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? '結果が見つかりません' : '選択肢がありません'
        }
        loadingMessage={() => "ローディング中..."}
        styles={{
          control: (base) => ({
            ...base,
            minHeight: height,
            width: width,
            padding: "0.15rem 0",
            borderRadius: "0.5rem",
            borderColor: error ? "red" : "rgba(145, 158, 171, 0.32)",
            boxShadow: "none",
            backgroundColor: isDisabled ? "#f4f6f8" : "#ffffff",
            "&:hover": {
              borderColor: "var(--primary-color)",
            },
          }),
          indicatorSeparator(base) {
            return {
              ...base,
              display: "none",
            };
          },
          dropdownIndicator(base, props) {
            return {
              ...base,
              transform: props.selectProps.menuIsOpen
                ? "rotate(180deg)"
                : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            };
          },
          menuList(base) {
            return {
              ...base,
              padding: "0",
              maxHeight: "194px",
            };
          },
          menu(base) {
            return {
              ...base,
              borderRadius: "0.5rem",
              marginTop: "0.25rem",
              overflow: "hidden",
              zIndex: 100,
            };
          },
          placeholder(base) {
            return {
              ...base,
              color: "rgb(156 163 175 / 0.8)",
              fontSize: "0.875rem",
              fontWeight: "400",
              lineHeight: "1.375rem",
              whiteSpace: "nowrap",
            };
          },
          option(base) {
            return {
              ...base,
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              fontWeight: "400",
              lineHeight: "1.375rem",
              color: "#212B36",
              backgroundColor: "#fff",
              cursor: "pointer",
              borderBottom: "1px solid rgba(145, 158, 171, 0.32)",
              "&:last-child": {
                borderBottom: "none",
              },
              "&:hover": {
                backgroundColor: "#F4F6F8",
              },
            };
          },
          multiValue(base, props) {
            return {
              ...base,
              padding: 0,
              backgroundColor: props.isDisabled ? "#e3e7ea" : "#F4F6F8",
              borderRadius: "0.25rem",
              fontSize: "0.875rem",
              fontWeight: "400",
              lineHeight: "1.375rem",
            };
          },
          multiValueLabel(base) {
            return {
              ...base,
              marginLeft: "0.25rem",
              padding: "0.25rem 0.5rem",
              fontSize: "0.875rem",
              fontWeight: "400",
              lineHeight: "1.375rem",
            };
          },
        }}
      />

      {error && <p className="text-error_main text-xs mt-1">{error}</p>}
    </div>
  );
};
